export enum SectionKeyOptions {
  OVERVIEW = 'accountOverview',
  ALERTS = 'alerts',
  BILLING = 'billable',
  SIGNATURE = 'signatureStatus',
  BATTERY_LIFE = 'batteryLife',
  DEVICE_RECALLS = 'deviceRecalls',
  LEADS_RECALLS = 'leadsRecalls'
}

export type SectionKey =
  | SectionKeyOptions.OVERVIEW
  | SectionKeyOptions.ALERTS
  | SectionKeyOptions.BILLING
  | SectionKeyOptions.SIGNATURE
  | SectionKeyOptions.BATTERY_LIFE
  | SectionKeyOptions.DEVICE_RECALLS
  | SectionKeyOptions.LEADS_RECALLS;

export interface AccountOverviewQuery {
  dateRange: [string, string];
  columns: Array<keyof AccountOverviewData>;
  clinicIds: string[];
  fetchAll?: boolean;
  limit?: number;
  offset?: number;
}

export interface AlertQuery {
  dateRange: [string, string];
  columns: Array<keyof AlertData>;
  clinicIds: string[];
  fetchAll?: boolean;
  limit?: number;
  offset?: number;
}

export interface BillingQuery {
  dateRange: [string, string];
  columns: Array<keyof BillingData>;
  clinicIds: string[];
  fetchAll?: boolean;
  limit?: number;
  offset?: number;
}

export interface SignaturesQuery {
  dateRange: [string, string];
  columns: Array<keyof SignatureData>;
  clinicIds: string[];
  fetchAll?: boolean;
  limit?: number;
  offset?: number;
  signedCountBy?: 'assignedUser' | 'signedUser';
}

export interface AccountOverviewResponse {
  data: AccountOverviewData[];
  pagination: Pagination;
}

export interface AlertResponse {
  data: AlertData[];
  pagination: Pagination;
}
export interface BillingResponse {
  data: BillingData[];
  pagination: Pagination;
}

export interface DownloadReportQuery {
  reportType: string;
  query: AccountOverviewQuery | AlertQuery | BillingQuery | SignaturesQuery;
}

export interface DownloadReportResponse {
  downloadUrl: string;
}

export interface SignaturesResponse {
  data: SignatureData[];
  pagination: Pagination;
}

export interface AnalyticsDataTableColumn {
  Header: string;
  accessor: keyof AccountOverviewData | keyof AlertData | keyof BillingData | keyof SignatureData;
  Cell?: ({ value, row }: { value?: any; row?: any }) => React.ReactNode;
  width?: number;
}

export interface AccountOverviewData {
  patientId: string;
  accountName: string;
  patientName: string;
  dateOfBirth: string;
  ehrMrn: string;
  diagnosis: string;
  vendor: string;
  deviceType: string;
  modelName: string;
  serialNumber: string;
  status: string;
  patientStatusReason: string;
  followingPractitioner: string;
  referringProvider: string;
}

export interface AlertData {
  patientId: string;
  billed: string;
  dateOfService: string;
  alertType: string;
  signedDate: string;
  accountName: string;
  patientName: string;
  dateOfBirth: string;
  ehrMrn: string;
  diagnosis: string;
  vendor: string;
  deviceType: string;
  modelName: string;
  serialNumber: string;
  status: string;
  patientStatusReason: string;
  followingPractitioner: string;
  referringProvider: string;
  signingUser: string;
}

export interface BillingData {
  accountName: string;
  patientName: string;
  dateOfBirth: string;
  ehrMrn: string;
  diagnosis: string;
  dateOfService: string;
  signedDate: string;
  vendor: string;
  deviceType: string;
  modelName: string;
  serialNumber: string;
  reportType: string;
  nextScheduledDate: string;
  followingPractitioner: string;
  referringProvider: string;
  professionalCptCode: string;
  technicalCptCode: string;
  signingPractitioner: string;
}

export interface SignatureData {
  followingPractitioner: string;
  redSigned: string;
  redAwaitingSignature: string;
  yellowSigned: string;
  yellowAwaitingSignature: string;
  greenSigned: string;
  greenAwaitingSignature: string;
}

export interface Pagination {
  limit: number;
  offset: number;
  fetchAll: boolean;
  total: number;
}

export type AccountFilterOption = {
  name: string;
  id: string;
  clinics?: AccountFilterOption[];
};

export interface ColumnSort {
  field: string;
  direction: 'asc' | 'desc';
}
export interface DeviceRecallQuery {
  sortBy: string; // default - accountName
  sortDirection: 'ASC' | 'DESC'; // default - DESC
  page: number; // default - 1
  limit: number; // default - 20, Max - 100
  filters?: {
    clinicIds?: string[];
    deviceType?: string[];
    vendor?: string[];
    recallStatus?: string[];
    recallClass?: string[];
    recallDateRange?: [string, string]; // [startDate, endDate]
  };
}

export interface DeviceRecallQueryParams {
  dateRange: [string, string];
  columns: string[];
  clinicIds: string[];
  limit: number;
  offset: number;
  fetchAll: boolean;
  filters: Array<{
    field: string;
    operator: string;
    arguments: string[];
  }>;
  orderBy?: {
    [key: number]: {
      field: string;
      direction: 'asc' | 'desc';
    };
  };
  sortDirection?: 'ASC' | 'DESC';
}

export interface DeviceRecallApiParams {
  sortDirection: 'ASC' | 'DESC';
  page: number;
  limit: number;
  filters?: {
    clinicIds?: string[];
    deviceVendor?: string[];
    deviceType?: string[];
    recallClass?: string[];
    recallStatus?: string[];
    leadType?: string[];
    recallDateRange?: [string, string];
  };
}

export interface BatteryLifeQueryParams {
  dateRange: [string, string];
  columns: string[];
  clinicIds: string[];
  limit: number;
  offset: number;
  fetchAll: boolean;
  filters: Array<{
    field: string;
    operator: string;
    arguments: string[] | number;
  }>;
  orderBy?: {
    [key: number]: {
      field: string;
      direction: 'asc' | 'desc';
    };
  };
  sortDirection?: 'ASC' | 'DESC';
}

export interface BatteryLifeApiParams {
  sortBy: string;
  sortDirection: 'ASC' | 'DESC';
  page: number;
  limit: number;
  filters?: {
    clinicIds?: string[];
    deviceType?: string[];
    deviceVendor?: string[];
    batteryLongevity?: {
      operator: '<' | '<=' | '>' | '>=' | '=';
      months: number;
    };
  };
}

export interface DeviceRecallData {
  id: string;
  recallStatus: string;
  recallClass: string;
  recallDate: string;
  modelName: string;
  deviceType: string;
  vendor: string;
  patientName: string;
  accountName: string;
}

export interface DeviceRecallResponse {
  data: DeviceRecallData[];
  pagination: {
    fetchAll: boolean;
    limit: number;
    offset: number;
    total: number;
  };
}

export interface LeadRecallQueryParams {
  dateRange: [string, string];
  columns: string[];
  clinicIds: string[];
  limit: number;
  offset: number;
  fetchAll: boolean;
  filters: Array<{
    field: string;
    operator: string;
    arguments: string[];
  }>;
  orderBy?: {
    [key: number]: {
      field: string;
      direction: 'asc' | 'desc';
    };
  };
  sortDirection?: 'ASC' | 'DESC';
}

export interface LeadRecallApiParams {
  sortDirection: 'ASC' | 'DESC';
  page: number;
  limit: number;
  filters?: {
    clinicIds?: string[];
    deviceVendor?: string[];
    deviceType?: string[];
    leadType?: string[];
    recallClass?: string[];
    recallStatus?: string[];
    recallDateRange?: [string, string];
  };
}

export interface LeadRecallData {
  id: string;
  recallStatus: string;
  recallClass: string;
  recallDate: string;
  deviceModelName: string;
  deviceType: string;
  vendor: string;
  patientName: string;
  accountName: string;
}

export interface LeadRecallResponse {
  data: LeadRecallData[];
  pagination: {
    fetchAll: boolean;
    limit: number;
    offset: number;
    total: number;
  };
}

export interface LeadRecallQuery {
  sortBy: string; // default - accountName
  sortDirection: 'ASC' | 'DESC'; // default - DESC
  page: number; // default - 1
  limit: number; // default - 20, Max - 100
  filters?: {
    clinicIds?: string[];
    deviceType?: string[];
    vendor?: string[];
    recallStatus?: string[];
    recallClass?: string[];
    recallDateRange?: [string, string]; // [startDate, endDate]
  };
}
export interface BatteryLifeResponse {
  data: BatteryLifeData[];
  pagination: {
    fetchAll: boolean;
    limit: number;
    offset: number;
    total: number;
  };
}
export interface BatteryLifeData {
  id: string;
  deviceType: string;
  vendor: string;
  deviceModelName: string;
  patientId: string;
  patientName: string;
  accountName: string;
  batteryPercentRemaining: number;
  batteryLongevity: number;
}

export interface BatteryLifeQuery {
  sortBy: string; // default - accountName
  sortDirection: 'ASC' | 'DESC'; // default - DESC
  page: number; // default - 1
  limit: number; // default - 20, Max - 100
  filters?: {
    clinicIds?: string[];
    deviceType?: string[]; // enum
    vendor?: string[]; // enum
    batteryLongevity?: {
      operator: string; // ['<', '<=', '>', '>=', '=']
      months: number;
    };
  };
}
